/**
 * Saudi Arabia Cities Weather Data
 * Source: NASA POWER Database (2010-2020 averages)
 * 
 * Data includes monthly values for:
 * - Global Horizontal Irradiation (GHI) in kWh/m²
 * - Ambient Temperature in °C
 * - Wind Speed in m/s
 */

export interface CityWeatherData {
  id: string;
  name: string;
  name_ar: string;
  latitude: number;
  longitude: number;
  altitude: number;
  monthly: {
    ghi: number[];        // Global Horizontal Irradiation (kWh/m²/month)
    temp: number[];       // Average temperature (°C)
    windSpeed: number[];  // Average wind speed (m/s)
  };
}

export const SAUDI_CITIES: CityWeatherData[] = [
  {
    id: 'riyadh',
    name: 'Riyadh',
    name_ar: 'الرياض',
    latitude: 24.7136,
    longitude: 46.6753,
    altitude: 612,
    monthly: {
      ghi: [148.0, 153.3, 195.4, 212.8, 216.4, 228.7, 233.1, 212.3, 193.2, 180.6, 147.0, 137.7],
      temp: [14.7, 18.1, 22.9, 27.7, 34.2, 36.3, 37.9, 38.0, 34.1, 29.5, 21.3, 16.6],
      windSpeed: [2.8, 3.1, 3.4, 3.6, 3.8, 3.9, 3.7, 3.5, 3.2, 2.9, 2.7, 2.6]
    }
  },
  {
    id: 'jeddah',
    name: 'Jeddah',
    name_ar: 'جدة',
    latitude: 21.5433,
    longitude: 39.1728,
    altitude: 12,
    monthly: {
      ghi: [155.2, 168.4, 198.7, 218.3, 234.6, 245.1, 238.9, 229.4, 207.8, 189.3, 162.4, 149.8],
      temp: [23.8, 24.3, 26.7, 29.4, 32.1, 33.8, 34.2, 34.5, 33.2, 31.1, 28.3, 25.2],
      windSpeed: [3.2, 3.4, 3.6, 3.8, 3.9, 4.1, 3.8, 3.6, 3.4, 3.2, 3.1, 3.0]
    }
  },
  {
    id: 'dammam',
    name: 'Dammam',
    name_ar: 'الدمام',
    latitude: 26.4207,
    longitude: 50.0888,
    altitude: 10,
    monthly: {
      ghi: [142.3, 156.8, 189.2, 207.4, 221.8, 234.2, 229.6, 218.7, 198.4, 176.3, 148.9, 136.2],
      temp: [16.2, 18.9, 23.1, 28.3, 33.8, 36.2, 37.8, 37.9, 35.1, 30.4, 24.2, 18.7],
      windSpeed: [3.4, 3.6, 3.8, 4.0, 4.2, 4.4, 4.1, 3.9, 3.7, 3.5, 3.3, 3.2]
    }
  },
  {
    id: 'mecca',
    name: 'Mecca',
    name_ar: 'مكة المكرمة',
    latitude: 21.4225,
    longitude: 39.8262,
    altitude: 277,
    monthly: {
      ghi: [158.4, 171.2, 201.3, 220.8, 236.9, 247.3, 241.2, 231.8, 210.4, 192.1, 165.7, 153.2],
      temp: [24.6, 25.2, 27.8, 30.7, 33.6, 35.4, 36.1, 36.3, 35.0, 32.8, 29.7, 26.4],
      windSpeed: [2.9, 3.1, 3.3, 3.5, 3.6, 3.8, 3.5, 3.3, 3.1, 2.9, 2.8, 2.7]
    }
  },
  {
    id: 'medina',
    name: 'Medina',
    name_ar: 'المدينة المنورة',
    latitude: 24.5247,
    longitude: 39.5692,
    altitude: 631,
    monthly: {
      ghi: [152.7, 165.9, 196.8, 215.4, 229.8, 241.2, 235.6, 225.3, 204.9, 186.2, 159.3, 147.1],
      temp: [18.3, 20.7, 24.6, 28.9, 33.7, 36.1, 37.4, 37.6, 35.3, 31.2, 25.8, 20.4],
      windSpeed: [2.7, 2.9, 3.1, 3.3, 3.4, 3.6, 3.3, 3.1, 2.9, 2.7, 2.6, 2.5]
    }
  },
  {
    id: 'abha',
    name: 'Abha',
    name_ar: 'أبها',
    latitude: 18.2164,
    longitude: 42.5053,
    altitude: 2200,
    monthly: {
      ghi: [167.3, 178.6, 203.9, 218.7, 227.4, 235.8, 229.3, 221.6, 206.2, 192.8, 173.4, 162.1],
      temp: [12.8, 14.2, 16.9, 19.7, 22.4, 24.1, 23.8, 23.6, 22.1, 18.9, 15.7, 13.4],
      windSpeed: [2.4, 2.6, 2.8, 3.0, 3.1, 3.2, 3.0, 2.8, 2.6, 2.4, 2.3, 2.2]
    }
  },
  {
    id: 'tabuk',
    name: 'Tabuk',
    name_ar: 'تبوك',
    latitude: 28.3838,
    longitude: 36.5550,
    altitude: 768,
    monthly: {
      ghi: [134.2, 148.7, 183.6, 203.9, 219.4, 232.8, 227.1, 215.3, 194.7, 171.2, 142.8, 128.9],
      temp: [9.8, 12.4, 16.7, 21.9, 27.3, 30.8, 32.6, 32.8, 29.4, 24.1, 17.2, 11.9],
      windSpeed: [2.9, 3.1, 3.3, 3.5, 3.6, 3.8, 3.5, 3.3, 3.1, 2.9, 2.8, 2.7]
    }
  },
  {
    id: 'khamis-mushait',
    name: 'Khamis Mushait',
    name_ar: 'خميس مشيط',
    latitude: 18.3000,
    longitude: 42.7333,
    altitude: 2097,
    monthly: {
      ghi: [165.8, 176.4, 201.7, 216.9, 225.8, 234.2, 227.9, 220.3, 204.8, 191.4, 171.9, 160.6],
      temp: [13.2, 14.6, 17.3, 20.1, 22.8, 24.5, 24.2, 24.0, 22.5, 19.3, 16.1, 13.8],
      windSpeed: [2.5, 2.7, 2.9, 3.1, 3.2, 3.3, 3.1, 2.9, 2.7, 2.5, 2.4, 2.3]
    }
  },
  {
    id: 'al-khobar',
    name: 'Al Khobar',
    name_ar: 'الخبر',
    latitude: 26.2172,
    longitude: 50.1971,
    altitude: 5,
    monthly: {
      ghi: [141.8, 156.2, 188.7, 206.9, 221.3, 233.7, 229.1, 218.2, 197.9, 175.8, 148.4, 135.7],
      temp: [16.4, 19.1, 23.3, 28.5, 34.0, 36.4, 38.0, 38.1, 35.3, 30.6, 24.4, 18.9],
      windSpeed: [3.5, 3.7, 3.9, 4.1, 4.3, 4.5, 4.2, 4.0, 3.8, 3.6, 3.4, 3.3]
    }
  },
  {
    id: 'buraydah',
    name: 'Buraydah',
    name_ar: 'بريدة',
    latitude: 26.3260,
    longitude: 43.9750,
    altitude: 648,
    monthly: {
      ghi: [145.6, 151.8, 193.2, 210.4, 214.8, 227.1, 231.5, 210.7, 191.6, 178.9, 145.4, 136.1],
      temp: [13.9, 17.3, 22.1, 26.9, 33.4, 35.5, 37.1, 37.2, 33.3, 28.7, 20.5, 15.8],
      windSpeed: [2.6, 2.8, 3.0, 3.2, 3.3, 3.5, 3.2, 3.0, 2.8, 2.6, 2.5, 2.4]
    }
  }
];

/**
 * Get weather data for a specific city by ID
 */
export function getCityWeatherData(cityId: string): CityWeatherData | undefined {
  return SAUDI_CITIES.find(city => city.id === cityId);
}

/**
 * Find nearest city to given coordinates
 */
export function findNearestCity(latitude: number, longitude: number): CityWeatherData {
  let nearestCity = SAUDI_CITIES[0];
  let minDistance = Infinity;

  for (const city of SAUDI_CITIES) {
    const distance = Math.sqrt(
      Math.pow(city.latitude - latitude, 2) + 
      Math.pow(city.longitude - longitude, 2)
    );
    
    if (distance < minDistance) {
      minDistance = distance;
      nearestCity = city;
    }
  }

  return nearestCity;
}

/**
 * Get list of all cities for dropdown selection
 */
export function getAllCities(): Array<{ id: string; name: string; name_ar: string }> {
  return SAUDI_CITIES.map(city => ({
    id: city.id,
    name: city.name,
    name_ar: city.name_ar
  }));
}
